import * as yup from 'yup';
import { CampaignFormValues } from '../../types';
import { assignEnrollmentSections, isFieldVisible, normalizeEnrollmentSchedules } from '../../helpers';
import { buildEnrollmentDefaultMessages, commonDefaults } from '../shared/campaignDefaults';
import {
  hasBaseErrors,
  hasCaptureErrors,
  hasInvalidJoinWindowValues,
  hasInvalidScheduledDelay,
  hasJoinWindowErrors,
  hasMissingGroups,
  hasMissingRequiredKeywords,
  hasMissingScheduledTime,
} from '../shared/primaryActionChecks';
import { validateMessageOrder } from '../shared/messageSchedule';
import { commonSchemaShape } from '../shared/validationSchema';
import { CampaignTypeSpec } from '../policy';
import { policyIssue } from '../issues';
import { inboundRecipients, standardFields } from './shared';

export const enrollmentSpec: CampaignTypeSpec = {
  externalType: 'custom',
  scheduleKind: 'enrollment',
  recipients: inboundRecipients,
  fields: standardFields,

  defaults: config => ({
    ...commonDefaults(config),
    joinStartAt: config.enrollmentSettings?.joinStartAt?.default || null,
    joinEndAt: config.enrollmentSettings?.joinEndAt?.default || null,
    scheduledMessages: buildEnrollmentDefaultMessages(config, { scheduledType: 'immediate' }),
  }),

  fromCampaign: (base, campaign) => ({
    ...base,
    scheduledMessages: normalizeEnrollmentSchedules(
      assignEnrollmentSections(base.scheduledMessages),
    ),
    joinStartAt: campaign.enrollmentWindows?.[0]?.startAt ?? campaign.joinStartAt ?? null,
    joinEndAt: campaign.enrollmentWindows?.[0]?.endAt ?? campaign.joinEndAt ?? null,
  }),

  issues: (values, config) => [
    ...policyIssue(
      hasBaseErrors(values, config)
      || hasJoinWindowErrors(values)
      || hasCaptureErrors(values)
      || hasInvalidScheduledDelay(values)
      || hasMissingScheduledTime(values)
      || (isFieldVisible(config.recipients) && hasMissingGroups(values))
      || hasMissingRequiredKeywords(values, config),
      'enrollment-primary-blocked',
    ),
    ...policyIssue(hasInvalidJoinWindowValues(values), 'invalid-join-window', {
      blocksDraft: true,
      blocksPrimaryAction: false,
    }),
  ],

  payload: ({ base, values, config }) => ({
    ...base,
    scheduledMessages: base.scheduledMessages.map(message => (message.position === 1
      ? { ...message, scheduledType: 'immediate' as const, time: null, delay: null }
      : message)),
    groups: values.groupIds ?? [],
    keywords: isFieldVisible(config.keywords) ? values.keywordIds : undefined,
    joinStartAt: values.joinStartAt ?? null,
    joinEndAt: values.joinEndAt ?? null,
  }),

  schema: config => yup
    .object({
      ...commonSchemaShape(config, { messages: true, groups: true }),
      joinStartAt: yup.string().nullable().required('Join window start is required.'),
      joinEndAt: yup.string().nullable().required('Join window end is required.'),
    })
    .test(
      'join-window-end-after-start',
      'Join window end must be after join window start',
      function (values) {
        const { joinStartAt, joinEndAt } = values as CampaignFormValues;
        if (!joinStartAt || !joinEndAt) return true;
        if (Date.parse(joinEndAt) > Date.parse(joinStartAt)) return true;
        return this.createError({ path: 'joinEndAt' });
      },
    )
    .test(
      'followup-after-join-window',
      'Follow-up message times must be after the join window end',
      function (values) {
        const { scheduledMessages, joinEndAt } = values as CampaignFormValues;
        if (!joinEndAt || !scheduledMessages?.length) return true;
        const joinEndMs = Date.parse(joinEndAt);
        const sectionedMessages = scheduledMessages.some(message => message.enrollmentSection)
          ? scheduledMessages
          : assignEnrollmentSections(scheduledMessages);
        const early = sectionedMessages.find(
          message => message.enrollmentSection === 'scheduled'
            && message.scheduledType === 'time'
            && message.time
            && Date.parse(message.time) <= joinEndMs,
        );
        if (!early) return true;
        const earlyIndex = scheduledMessages.findIndex(message => message.position === early.position);
        return this.createError({
          path: `scheduledMessages[${earlyIndex}].time`,
          message: 'Follow up message time must be later than the join window end time.',
        });
      },
    )
    .test(
      'message-order',
      'Messages must be in chronological order',
      values => {
        const { scheduledMessages, dateScheduled } = values as CampaignFormValues;
        if (!scheduledMessages?.length) return true;
        return validateMessageOrder(scheduledMessages, dateScheduled).valid;
      },
    ),
};
